import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useEffectEvent } from 'use-effect-event';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import {
  useCurrentRightPanelName,
  useRightPanel,
} from '../app/layout/RightPanel';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { Banner } from '../components/BannerModalContent';
import { DECLINE_ACCESS_TO, GRANT_ACCESS_TO } from '../constants/params';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { TopicPrivacy } from '../graphql/generated/graphql';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { topicToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import PageMeta from '../utils/PageMeta';
import handleApolloError from '../utils/error/handleApolloError';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import topicPath from '../utils/url/topicPath';
import useIsEditingInPlace from '../utils/useIsEditingInPlace';
import useResettableState from '../utils/useResettableState';
import TopicAccess from './TopicAccess';
import * as styles from './TopicView.module.scss';
import TopicViewHeader from './TopicViewHeader';
import TopicPosts from './posts/TopicPosts';
import topicPolicyCheck, { TopicPolicyAction } from './topicPolicyCheck';
import { useAllTopicMap } from './useAllTopics';
import {
  useRemoveTopicUser,
  useUpdateTopicUser,
} from './useTopicUserMutations';
import TopicUsersPanel from './users/TopicUsersPanel';
import sortTopic from './utils/sortTopic';

const TOPIC_VIEW_TOPIC_FRAGMENT = gql(`
  fragment TopicView_topic on Topic {
    id
    name
    description
    banner {
      original
    }
    defaultShow
    defaultSort
  }
`);

export type EditTopicPayload = DocumentType<typeof TOPIC_VIEW_TOPIC_FRAGMENT>;

export type EditTopicState = Pick<
  EditTopicPayload,
  'name' | 'description' | 'defaultShow' | 'defaultSort'
> & {
  banner: Banner | null;
};

const STATE_KEY_VIEW_MEMBERS = 'viewMembers';
const RIGHT_PANEL_NAME = 'topicUsers';

function getInitialState(topic: EditTopicPayload): EditTopicState {
  return {
    name: topic.name,
    description: topic.description,
    banner: topic.banner?.original ? { image: topic.banner.original } : null,
    defaultShow: topic.defaultShow,
    defaultSort: topic.defaultSort,
  };
}

const TOPIC_QUERY = gql(/** @complexity 17 */ `
  query TopicView($id: ID!) {
    topic(id: $id) {
      id
      privacy
      children {
        id
      }
      ...TopicView_topic
      ...TopicViewHeader_topic
      ...TopicPosts_topic
      ...topicPath_topic
    }
  }
`);

const useMembershipEffect = () => {
  const dispatch = useAppDispatch();
  const [topicId] = useSlug('topicSlug');
  const removeTopicUser = useRemoveTopicUser();
  const updateTopicUser = useUpdateTopicUser();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const grantAccessTo = searchParams.get(GRANT_ACCESS_TO);
    const declineAccessTo = searchParams.get(DECLINE_ACCESS_TO);

    if (grantAccessTo) {
      setSearchParams({}, { replace: true });

      updateTopicUser(
        { id: topicId },
        { id: grantAccessTo },
        'requestor',
        'member',
      )
        .then(({ data }) => {
          if (data?.user?.name) {
            dispatch(
              UIActions.pushToast(
                topicToasts.memberAdded({
                  userName: data.user.name,
                }),
                { duration: 7000 },
              ),
            );
          }
        })
        .catch((error) => {
          handleApolloError(error, 'NOT_FOUND', () =>
            dispatch(
              UIActions.pushToast(topicToasts.requestRejectedWarning, {
                duration: 7000,
              }),
            ),
          );
        });
    }

    if (declineAccessTo) {
      setSearchParams({}, { replace: true });

      removeTopicUser({ id: topicId }, { id: declineAccessTo }).then(
        ({ data }) => {
          if (data?.user?.name) {
            dispatch(
              UIActions.pushToast(
                topicToasts.requestDeclinedWarning({
                  userName: data.user.name,
                }),
                { duration: 7000 },
              ),
            );
          }
        },
      );
    }
  }, [
    setSearchParams,
    searchParams,
    topicId,
    dispatch,
    removeTopicUser,
    updateTopicUser,
  ]);
};

const TopicView = () => {
  const [topicId] = useSlug('topicSlug');
  const topicMap = useAllTopicMap();
  const user = useAppSelector((state) => state.session.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useIsEditingInPlace();

  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  useEffect(() => {
    if (isMobile && isEditing) {
      setIsEditing(false);
    }
  }, [isMobile, isEditing, setIsEditing]);

  useMembershipEffect();

  const { error, data } = useQuery(TOPIC_QUERY, { variables: { id: topicId } });

  useErrorRedirectEffect({ noun: 'topic', error });

  const topicForPermissionCheck = topicMap[topicId];

  const topic = useMemo(
    () =>
      data && {
        ...data.topic,
        privacy: data.topic.privacy,
        children: _.compact(
          data.topic.children.map(({ id }) => topicMap[id]),
        ).toSorted(sortTopic),
      },
    [data, topicMap],
  );

  const { openRightPanel, closeRightPanel } = useRightPanel();
  const currentRightPanelName = useCurrentRightPanelName();

  const onLocationChange = useEffectEvent(() => {
    if (location.state?.[STATE_KEY_VIEW_MEMBERS]) {
      openRightPanel(<TopicUsersPanel />, { name: RIGHT_PANEL_NAME });
    } else if (currentRightPanelName === RIGHT_PANEL_NAME) {
      closeRightPanel();
    }
  });
  useEffect(() => {
    onLocationChange();
  }, [location.key]);

  const topicPathUrl = topic ? topicPath(topic) : null;

  useEffect(() => {
    if (topicPathUrl && location.state?.new) {
      if (isEditing) {
        closeRightPanel();
        return;
      }
      if (
        topic?.privacy === TopicPrivacy.Secret ||
        topic?.privacy === TopicPrivacy.Private
      ) {
        navigate(topicPathUrl);
        openRightPanel(<TopicUsersPanel />, { name: RIGHT_PANEL_NAME });
      }
    }
  }, [
    isEditing,
    navigate,
    location.state?.new,
    topic?.privacy,
    topicPathUrl,
    openRightPanel,
    closeRightPanel,
  ]);

  const [
    editTopicState,
    { update: updateEditTopicState, reset: resetEditTopicState },
  ] = useResettableState<EditTopicState | null>(null);

  const resetTopicState = useCallback(
    (t: EditTopicPayload) => resetEditTopicState(getInitialState(t)),
    [resetEditTopicState],
  );

  useEffect(() => {
    if (topic) {
      resetTopicState(topic);
    }
  }, [isEditing, resetTopicState, topic]);

  if (topic == null || topicForPermissionCheck == null) return null;

  const canViewPosts = topicPolicyCheck(
    TopicPolicyAction.ViewPost,
    topicForPermissionCheck,
    user,
  );

  return (
    <PageLayout.Content
      key={topic.id}
      className={classNames(styles.container, {
        [styles.isEditing]: isEditing,
      })}
      noPaddingX
      noPaddingY
    >
      <PageMeta title={`${topic.name} - Slab`} />
      <TopicViewHeader
        topic={topic}
        editing={isEditing}
        setIsEditing={setIsEditing}
        editTopicState={editTopicState}
        updateEditTopicState={updateEditTopicState}
        resetEditTopicState={resetTopicState}
      />
      <TwoColumnRow
        full={
          canViewPosts ? (
            <TopicPosts
              topic={topic}
              editing={isEditing}
              editTopicState={editTopicState}
              updateEditTopicState={updateEditTopicState}
            />
          ) : (
            <TopicAccess topic={topicForPermissionCheck} />
          )
        }
      />
    </PageLayout.Content>
  );
};

export default TopicView;
